import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import styles from './popup.module.css';
import classnames from 'classnames';
import React from 'react';

export const Popup = forwardRef(({ children }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, direction: 'right' });
  const [popupContentHeight, setPopupContentHeight] = useState(0);
  const wrapperRef = useRef(null);
  const popupRef = useRef(null);
  const popupContentRef = useRef(null);
  const subscribers = useRef(new Set());

  const subscribe = (callback) => {
    subscribers.current.add(callback);
    return () => subscribers.current.delete(callback);
  };

  useImperativeHandle(ref, () => ({
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((prev) => !prev),
    updatePosition: calculatePosition,
    subscribe,
  }));

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setPopupContentHeight(entry.contentRect.height);
      }
    });

    if (popupContentRef.current) {
      observer.observe(popupContentRef.current);
    }

    return () => observer.disconnect();
  }, [popupContentRef.current]);

  useEffect(() => {
    subscribers.current.forEach((callback) => callback(isOpen));
  }, [isOpen]);

  const getPopupHeight = () => {
    return popupRef.current?.offsetHeight || 200;
  };

  const getPopupWidth = () => {
    return popupRef.current?.offsetWidth || 450;
  };

  const isElementObscured = () => {
    if (!wrapperRef.current) return true;

    const element = wrapperRef.current;
    const rect = element.getBoundingClientRect();
    const elementsAtPoint = document.elementsFromPoint(
      rect.left + rect.width / 2,
      rect.top + rect.height / 2,
    );

    return !elementsAtPoint.some((el) => el === element || element.contains(el));
  };

  const checkElementVisibility = () => {
    if (!wrapperRef.current) return false;

    const rect = wrapperRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= viewportHeight &&
      rect.right <= viewportWidth &&
      !isElementObscured()
    );
  };

  const calculatePosition = () => {
    if (!wrapperRef.current) return;

    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const popupHeight = getPopupHeight();
    const popupWidth = getPopupWidth();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const height = document.getElementById('content_popup').offsetHeight;

    const heightContent = viewportHeight - height;

    let newSize = null;

    if (heightContent <= 0) {
      newSize = {
        top: 100,
      };
    } else if (wrapperRect.top < heightContent) {
      newSize = {
        top: wrapperRect.top,
      };
    } else {
      newSize = {
        top: heightContent,
      };
    }

    const space = {
      right: viewportWidth - wrapperRect.right,
      bottom: viewportHeight - wrapperRect.bottom,
      left: wrapperRect.left,
      top: wrapperRect.top,
    };

    if (space.right >= popupWidth) {
      setPosition({
        top: newSize.top,
        left: wrapperRect.right + 5,
        direction: 'right',
      });
    } else {
      setPosition({
        top: newSize.top,
        left: wrapperRect.left - popupWidth - 10,
        direction: 'left',
      });
    }
  };

  const updatePopupState = () => {
    const isVisible = checkElementVisibility();
    if (!isVisible) {
      setIsOpen(false);
    } else if (isOpen) {
      calculatePosition();
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePopupState();
    }
  }, [isOpen, popupContentHeight]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEvents = () => {
      updatePopupState();
    };

    window.addEventListener('scroll', handleEvents, true);
    window.addEventListener('resize', handleEvents);

    return () => {
      window.removeEventListener('scroll', handleEvents, true);
      window.removeEventListener('resize', handleEvents);
    };
  }, [isOpen]);

  const toggleOpen = (e) => {
    e.stopPropagation();
    if (!isOpen) {
      updatePopupState();
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const gtePopupPortalContent = () => {
    const footer = React.Children.toArray(children).find(
      (child) => React.isValidElement(child) && child.type === Popup.Portal,
    );

    if (footer) return footer;

    return null;
  };

  const content = React.Children.toArray(children).filter(
    (child) => !(React.isValidElement(child) && child.type === Popup.Portal),
  );

  return (
    <div className={classnames(styles.container)} ref={wrapperRef} onClick={toggleOpen}>
      <div className={styles.content}>
        {content}
        {isOpen && (
          <div className={classnames(styles.popupArrow, styles[`arrow-${position.direction}`])} />
        )}
      </div>

      {isOpen && (
        <div
          className={classnames(styles.popup, styles[position.direction])}
          ref={popupRef}
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${getPopupWidth()}px`,
            maxWidth: '460px',
            maxHeight: `calc(100dvh - ${position.top}px - 8%)`,
            overflowY: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.popupContent} id="content_popup" ref={popupContentRef}>
            {gtePopupPortalContent()}
          </div>
        </div>
      )}
    </div>
  );
});

Popup.Portal = function PopupPortal({ children }) {
  return children;
};

Popup.Portal.displayName = 'PopupPortal';

export default Popup;
